/**
 * pathfinding.ts — galaxy-graph BFS helpers for AI multi-hop dispatch.
 *
 * v4M5.5 — supports the continuous Jump Area model per
 * `HYPERSPACE.md §1 + §4`. The AI dispatchers want to issue jumps
 * toward strategic destinations (colonise targets, enemy systems,
 * unscouted neighbours) that may sit beyond a single hop of the
 * fleet's hyperdrive range. This module finds the shortest
 * range-respecting path through the `state.systems` adjacency graph
 * and returns the first hop, so the AI can dispatch a stepping-stone
 * jump and re-evaluate on the next AI tick.
 *
 * Pure reader — no state mutation. Determinism: BFS frontier processed
 * in ascending systemId order; tie-breaks among equal-length paths
 * resolve to the lowest-id stepping stone.
 */

import type { GameState, SystemId } from '../state/types/index';
import { aiHazardPenaltyForSystem } from './balance-environment';

/**
 * Find the first system to jump to in order to reach `targetSystemId`
 * from `sourceSystemId`, respecting per-hop fleet range. Returns:
 *   - null if source or target don't exist in state.systems
 *   - null if source === target (no jump needed)
 *   - null if no range-respecting path exists
 *   - target itself if it's a direct in-range neighbour of source
 *   - some intermediate adjacent system otherwise (multi-hop case)
 *
 * `fleetRange` is the empire-scaled hyperdrive range — `min over fleet
 * ships of (template.hyperdriveRange × hyperdriveRangeMultiplier)`.
 * Same formula as `applyJumpShips` so the AI's pathfinder respects
 * the same gate the applier does.
 */
export function firstStepTowards(
  state: GameState,
  sourceSystemId: SystemId,
  targetSystemId: SystemId,
  fleetRange: number,
): SystemId | null {
  if (sourceSystemId === targetSystemId) return null;
  if (!Number.isFinite(fleetRange) || fleetRange <= 0) return null;
  const src = state.systems.get(sourceSystemId);
  const dst = state.systems.get(targetSystemId);
  if (!src || !dst) return null;
  const fleetRangeSq = fleetRange * fleetRange;

  // BFS with a parent map. Frontier is FIFO; each system explored once.
  // We process neighbours in ascending systemId order for determinism.
  const parent = new Map<SystemId, SystemId>();
  parent.set(sourceSystemId, sourceSystemId); // sentinel — source is its own parent
  const queue: SystemId[] = [sourceSystemId];
  let found = false;
  while (queue.length > 0) {
    const cur = queue.shift()!;
    if (cur === targetSystemId) {
      found = true;
      break;
    }
    const sys = state.systems.get(cur);
    if (!sys) continue;
    // Sort neighbours ascending for determinism. Copy so we don't mutate.
    const neighbours = [...sys.adjacentSystemIds].sort((a, b) => a - b);
    for (const nextId of neighbours) {
      if (parent.has(nextId)) continue;
      const next = state.systems.get(nextId);
      if (!next) continue;
      // Range gate per hop, same formula as applyJumpShips.
      const dx = next.x - sys.x;
      const dy = next.y - sys.y;
      if (dx * dx + dy * dy > fleetRangeSq) continue;
      parent.set(nextId, cur);
      queue.push(nextId);
    }
  }
  if (!found) return null;
  // Walk parents back from target to source; the first step is the
  // system whose parent is source.
  let step = targetSystemId;
  let prev = parent.get(step);
  while (prev !== undefined && prev !== sourceSystemId) {
    step = prev;
    prev = parent.get(step);
  }
  if (prev === undefined) return null; // unreachable safety
  return step;
}

// ---------------------------------------------------------------------------
// v4M8.2c — weighted pathing (AI.md §5.2)
// ---------------------------------------------------------------------------

/**
 * v4M8.2c — weighted-edge counterpart to {@link firstStepTowards}.
 *
 * AI.md §5.2 specifies multi-hop dispatch should use edge weights that
 * reflect both fuel cost and travel time:
 *
 *   edgeWeight(a → b) = hopDistance × (fuelCostPerUnit + α × travelTimePerUnit)
 *
 * where `α = pathFuelWeight` is the per-archetype tunable from
 * `EffectiveBehaviour`. This pushes path selection toward fuel-cheap
 * stepping stones when the archetype values frugality and toward
 * fast paths when α is high.
 *
 * Implementation: Dijkstra (single-source shortest path with
 * non-negative edge weights). Range-respecting per hop — neighbours
 * beyond `fleetRange` are excluded the same way as `firstStepTowards`.
 * A* with a Euclidean heuristic to the target would also work, but
 * Dijkstra is simpler and the galaxy is small enough (≤ 30-ish
 * systems) that the constant factor difference doesn't matter.
 *
 * **Caller responsibilities**:
 *   - `fuelCostPerUnit` — the dispatching fleet's per-distance fuel
 *     cost. Typically the slowest-fleet ship's
 *     `template.hyperdriveFuelUsePerUnit × techMul`. Pre-computed at
 *     the caller so this function stays free of `state.empires` /
 *     research-multiplier concerns.
 *   - `travelTimePerUnit` — the dispatching fleet's per-distance
 *     travel time. Typically `1 / (template.hyperdriveSpeed × techMul)`
 *     for the slowest-fleet ship.
 *   - `pathFuelWeight` — α from EffectiveBehaviour.
 *
 * **Determinism**: ties on edge weight resolve by ascending neighbour
 * id (same convention as `firstStepTowards`). The Dijkstra priority
 * queue is a plain sorted array kept short — galaxy sizes don't
 * justify a heap, and the sort gives O(n log n) behaviour with
 * deterministic ordering.
 *
 * **NOT yet consumed by dispatch.** Legacy `firstStepTowards` (BFS,
 * shortest-hop-count) remains the active path-finder through the
 * v4M8.2 strict-parity window. v4M8.x consumer flip switches dispatch
 * over.
 *
 * **Note on "BFS-equivalence at α === 0":** the v4M8.2 plan claimed
 * this collapses to BFS hop-count when α === 0. That's only true when
 * `fuelCostPerUnit` is also 0 (all edges weight 0 — degenerate).
 * Realistic α === 0 with nonzero fuel cost still picks shortest-
 * *distance* paths, which can differ from BFS shortest-*hop-count*.
 * The legacy `firstStepTowards` BFS is preserved unchanged for the
 * pure hop-count case; this function is the weighted-edge variant.
 */
export function firstStepTowardsWeighted(
  state: GameState,
  sourceSystemId: SystemId,
  targetSystemId: SystemId,
  fleetRange: number,
  fuelCostPerUnit: number,
  travelTimePerUnit: number,
  pathFuelWeight: number,
): SystemId | null {
  if (sourceSystemId === targetSystemId) return null;
  if (!Number.isFinite(fleetRange) || fleetRange <= 0) return null;
  if (!Number.isFinite(fuelCostPerUnit) || fuelCostPerUnit < 0) return null;
  if (!Number.isFinite(travelTimePerUnit) || travelTimePerUnit < 0) return null;
  if (!Number.isFinite(pathFuelWeight) || pathFuelWeight < 0) return null;
  const src = state.systems.get(sourceSystemId);
  const dst = state.systems.get(targetSystemId);
  if (!src || !dst) return null;
  const fleetRangeSq = fleetRange * fleetRange;

  // Dijkstra. `dist` is the shortest known weighted distance from
  // source to each system. `parent` lets us walk the path back.
  const dist = new Map<SystemId, number>();
  const parent = new Map<SystemId, SystemId>();
  dist.set(sourceSystemId, 0);
  parent.set(sourceSystemId, sourceSystemId); // sentinel

  // Open set as a plain array — galaxy sizes make a heap unnecessary.
  // Each iteration pops the lowest-dist entry; ties broken by
  // ascending systemId for determinism.
  const open: Array<{ id: SystemId; d: number }> = [
    { id: sourceSystemId, d: 0 },
  ];

  while (open.length > 0) {
    // Sort ascending by (d, id) and pull the head.
    open.sort((a, b) => (a.d !== b.d ? a.d - b.d : a.id - b.id));
    const cur = open.shift()!;
    if (cur.id === targetSystemId) break;
    // Stale-entry skip: if we've found a better path since this entry
    // was added, ignore it. (We don't have decrease-key in this array,
    // so we tolerate duplicates and rely on the dist-map check below.)
    const curBest = dist.get(cur.id);
    if (curBest !== undefined && cur.d > curBest) continue;

    const sys = state.systems.get(cur.id);
    if (!sys) continue;

    // Sort neighbours for tie-break determinism on equal weights.
    const neighbours = [...sys.adjacentSystemIds].sort((a, b) => a - b);
    for (const nextId of neighbours) {
      const next = state.systems.get(nextId);
      if (!next) continue;
      // Range gate per hop, same formula as applyJumpShips.
      const dx = next.x - sys.x;
      const dy = next.y - sys.y;
      const hopDistSq = dx * dx + dy * dy;
      if (hopDistSq > fleetRangeSq) continue;
      const hopDist = Math.sqrt(hopDistSq);
      // AI.md §5.2 edge weight formula.
      // v4M13.4d — hazard penalty per `TERRAIN_FEATURES.md §5.1`.
      // Adds the destination system's hazard score (sum of
      // `AI_HAZARD_PENALTY` per environment) to the edge weight.
      // Multiplied by hopDist so it scales with the segment length —
      // a hazardous one-hop shortcut still wins over a long
      // hazard-free detour when the hazard penalty is moderate.
      // Returns 0 for safe systems (fast-path; doesn't disturb the
      // existing AI.md §5.2 formula).
      const hazardPenalty = aiHazardPenaltyForSystem(state, nextId);
      const edgeWeight =
        hopDist *
        (fuelCostPerUnit + pathFuelWeight * travelTimePerUnit + hazardPenalty);
      const tentative = cur.d + edgeWeight;
      const known = dist.get(nextId);
      if (known === undefined || tentative < known) {
        dist.set(nextId, tentative);
        parent.set(nextId, cur.id);
        open.push({ id: nextId, d: tentative });
      }
    }
  }

  if (!parent.has(targetSystemId)) return null;
  // Walk parents back from target to source; the first step is the
  // system whose parent is source.
  let step = targetSystemId;
  let prev = parent.get(step);
  while (prev !== undefined && prev !== sourceSystemId) {
    step = prev;
    prev = parent.get(step);
  }
  if (prev === undefined) return null;
  return step;
}
